import React, { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import {
    FormControlLabel,
    Radio,
    RadioGroup as MUIRadioGroup,
    Typography,
    FormControl,
    InputLabel,
    FormHelperText,
    Stack,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import {
    ComboBox,
    GroupSelect,
    StepperControls,
    Select,
    StepWrapper,
    StyledCardSection,
    TextField,
} from '@fnol/library/src/components';
import { common, maxCharacterLimit, prepareCitiesList, prepareCountriesList } from '@fnol/library/src/lib';
import { useApi } from '@fnol/library/src/hooks';
import { prepareCausesList } from '../../lib/helpers';
import { getCountries, getCities, getCauses } from '../../rpc/actions';
import { translations, question50MaxLength } from '../../lib/consts';

type GenericObject = { [key: string]: any };

type Props = {
    navigator: GenericObject[];
    values: GenericObject[];
};

const ClaimDetailsScreen = ({ navigator }: Props) => {
    const { values, setTouched, setErrors, handleChange, touched, errors, setFieldValue } = useFormikContext<any>();

    const { response: countries, fetchMethod: fetchCountries } = useApi(getCountries);
    const { response: cities, fetchMethod: fetchCities } = useApi(getCities);
    const { response: causes, fetchMethod: fetchCauses } = useApi(getCauses);

    const [preparedCountries, setPreparedCountries] = useState<any>();
    const [preparedCities, setPreparedCities] = useState<any>();
    const [preparedCauses, setPreparedCauses] = useState<any>();

    const [currentStep, { goForward, goBack }] = navigator;

    const reportedClaimWithCause = values.reportedClaims.find(
        (claim: GenericObject) => +claim.idUzrokStete === values.cause,
    );

    const handleNext = (): void => {
        let valid = true;
        setTouched(
            {
                country: true,
                city: true,
                cityInSerbia: true,
                street: true,
                cause: true,
                vehicleParked: true,
                euReport: true,
                reportedToPolice: true,
                policeOnScene: true,
                policeInspection: true,
                alcoholTest: true,
                howDidDamageOccur: true,
            },
            false,
        );
        const existingErrors: GenericObject = {};
        if (values.country === '') {
            existingErrors.country = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.country !== '197' && values.country !== 197 && values.city === '') {
            existingErrors.city = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (
            (values.country === '197' || values.country === 197) &&
            (!values.cityInSerbia || values.cityInSerbia.id === '')
        ) {
            existingErrors.cityInSerbia = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.street === '') {
            existingErrors.street = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.cause === '') {
            existingErrors.cause = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if ([1, 3, 4, 10, 11, 12].includes(values.cause) && values.vehicleParked === '') {
            existingErrors.vehicleParked = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.cause === 1 && values.euReport === '') {
            existingErrors.euReport = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.reportedToPolice === '') {
            existingErrors.reportedToPolice = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (
            [1, 2, 13, 14].includes(values.cause) &&
            values.reportedToPolice === 'true' &&
            values.policeOnScene === ''
        ) {
            existingErrors.policeOnScene = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (
            [1, 2, 13, 14].includes(values.cause) &&
            values.policeOnScene === 'true' &&
            values.policeInspection === ''
        ) {
            existingErrors.policeInspection = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.howDidDamageOccur === '') {
            existingErrors.howDidDamageOccur = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        setErrors(existingErrors);

        if (valid) goForward();
    };

    const handleGoBack = (): void => {
        goBack();
    };

    const handleChangeCountry = (e: { target: { value: any } }): any => {
        setFieldValue('city', '');
        setFieldValue('cityInSerbia', '');
        setFieldValue('street', '');
        setFieldValue('country', e.target.value);
    };

    const handleChangeCause = (e: { target: { value: any } }): any => {
        setFieldValue('vehicleParked', '');
        setFieldValue('euReport', '');
        setFieldValue('reportedToPolice', '');
        setFieldValue('policeOnScene', '');
        setFieldValue('policeInspection', '');
        setFieldValue('alcoholTest', '');
        setFieldValue('cause', e.target.value);
    };

    useEffect(() => {
        fetchCountries();
        fetchCities();
        fetchCauses();
    }, []);

    useEffect(() => {
        Array.isArray(countries?.data?.data) && setPreparedCountries(prepareCountriesList(countries.data.data));
    }, [countries]);

    useEffect(() => {
        Array.isArray(cities?.data?.data) && setPreparedCities(prepareCitiesList(cities.data.data));
    }, [cities]);

    useEffect(() => {
        Array.isArray(causes?.data?.data) && setPreparedCauses(prepareCausesList(causes.data.data));
    }, [causes]);

    const selectedCountry = preparedCountries?.find((item: any) => item.id === parseInt(values.country));
    values.myCountry = selectedCountry?.text;

    const selectedCause = preparedCauses?.find((item: any) => item.id === values.cause);
    values.myCause = selectedCause?.text;

    const RadioGroup = (params: any) => {
        return (
            <MUIRadioGroup {...params} row>
                <FormControlLabel value={true} control={<Radio />} label="Da" />
                <FormControlLabel value={false} control={<Radio />} label="Ne" />
            </MUIRadioGroup>
        );
    };

    return (
        <StepWrapper>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="caption">
                    {translations.PLACE_OF_OCCURANCE}
                </Typography>
                <FormControl>
                    <InputLabel>{translations.COUNTRY}</InputLabel>
                    <Select
                        name="country"
                        items={preparedCountries || []}
                        value={values.country}
                        label={translations.COUNTRY}
                        onChange={handleChangeCountry}
                    />
                </FormControl>
                {values.country !== '197' && values.country !== 197 ? (
                    <FormControl>
                        <Field
                            name="city"
                            label={translations.CITY}
                            as={TextField}
                            inputProps={{
                                maxLength: question50MaxLength,
                            }}
                            error={!!(touched.city && errors.city)}
                        />
                        {touched.city && errors.city ? (
                            <FormHelperText error={true}>
                                <>{errors.city}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>
                ) : (
                    <FormControl error={!!(touched.cityInSerbia && errors.cityInSerbia)}>
                        <Field
                            name="cityInSerbia"
                            label={translations.CITY}
                            options={
                                preparedCities || [
                                    {
                                        id: '',
                                        value: '',
                                        text: '',
                                        code: '',
                                    },
                                ]
                            }
                            onChange={handleChange}
                            defaultValue={values.cityInSerbia}
                            as={ComboBox}
                            error={!!(touched.cityInSerbia && errors.cityInSerbia)}
                        />
                        {touched.cityInSerbia && errors.cityInSerbia ? (
                            <FormHelperText error={true}>
                                <>{errors.cityInSerbia}</>
                            </FormHelperText>
                        ) : null}
                    </FormControl>
                )}
                <FormControl>
                    <Field
                        name="street"
                        label={translations.STREET}
                        as={TextField}
                        inputProps={{
                            maxLength: question50MaxLength,
                        }}
                        error={!!(touched.street && errors.street)}
                    />
                    {touched.street && errors.street ? (
                        <FormHelperText error={true}>
                            <>{errors.street}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            </StyledCardSection>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="caption">
                    {translations.CIRCUMSTANCES_OF_DAMAGE}
                </Typography>
                <Typography style={{ fontSize: '16px' }} variant="caption">
                    {translations.CAUSE_OF_DAMAGE}
                </Typography>
                <FormControl>
                    <Field
                        name="cause"
                        items={preparedCauses || []}
                        label={translations.PLEASE_PICK}
                        value={values.cause}
                        as={GroupSelect}
                        onChange={handleChangeCause}
                        error={!!(touched.cause && errors.cause)}
                    />
                    {touched.cause && errors.cause ? (
                        <FormHelperText error={true}>
                            <>{errors.cause}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
                {!!reportedClaimWithCause && values.reportedClaims.length !== 0 && (
                    <Stack direction="row" alignItems="top" spacing={2} marginTop={2} marginBottom={3}>
                        <Info color="primary" />
                        <Typography variant="body2" paddingTop={0.5}>
                            {translations.CLAIM_REPORTED}
                        </Typography>
                    </Stack>
                )}
                {[1, 3, 4, 10, 11, 12].includes(values.cause) && (
                    <>
                        <FormControl>
                            <Typography style={{ fontSize: '16px' }} variant="caption">
                                {translations.VEHICLE_PARKED}
                            </Typography>

                            <Field type="boolean" name="vehicleParked" as={RadioGroup} />
                            <FormHelperText error={true}>
                                <>{errors.vehicleParked}</>
                            </FormHelperText>
                        </FormControl>
                    </>
                )}
                {values.cause === 1 && (
                    <>
                        <FormControl>
                            <Typography style={{ fontSize: '16px' }} variant="caption">
                                {translations.EU_TRAFFIC_ACCIDENT}
                            </Typography>

                            <Field type="boolean" name="euReport" as={RadioGroup} />
                            <FormHelperText error={true}>
                                <>{errors.euReport}</>
                            </FormHelperText>
                        </FormControl>
                    </>
                )}
                <>
                    <FormControl>
                        <Typography style={{ fontSize: '16px' }} variant="caption">
                            {translations.WAS_POLICE_NOTIFIED}
                        </Typography>

                        <Field type="boolean" name="reportedToPolice" as={RadioGroup} />
                        <FormHelperText error={true}>
                            <>{errors.reportedToPolice}</>
                        </FormHelperText>
                    </FormControl>
                </>
                {[1, 2, 13, 14].includes(values.cause) && values.reportedToPolice === 'true' && (
                    <>
                        <FormControl>
                            <Typography style={{ fontSize: '16px' }} variant="caption">
                                {translations.POLICE_ON_SCENE}
                            </Typography>

                            <Field type="boolean" name="policeOnScene" as={RadioGroup} />
                            <FormHelperText error={true}>
                                <>{errors.policeOnScene}</>
                            </FormHelperText>
                        </FormControl>
                    </>
                )}
                {[1, 2, 13, 14].includes(values.cause) &&
                    values.reportedToPolice === 'true' &&
                    values.policeOnScene === 'true' && (
                        <>
                            <FormControl>
                                <Typography style={{ fontSize: '16px' }} variant="caption">
                                    {translations.POLICE_INVESTIGATION}
                                </Typography>

                                <Field type="boolean" name="policeInspection" as={RadioGroup} />
                                <FormHelperText error={true}>
                                    <>{errors.policeInspection}</>
                                </FormHelperText>
                            </FormControl>
                        </>
                    )}
                {/* <Typography style={{ fontSize: '16px' }} variant="caption">
                    {translations.DESCRIBE_EVENT}
                </Typography> */}
                <FormControl>
                    <Field
                        name="howDidDamageOccur"
                        multiline={true}
                        label={translations.DESCRIBE_EVENT}
                        as={TextField}
                        maxRows={5}
                        minRows={5}
                        inputProps={{
                            maxLength: maxCharacterLimit,
                        }}
                        value={values.howDidDamageOccur}
                        helperText={`Preostalo karaktera ${maxCharacterLimit - values.howDidDamageOccur.length}`}
                        error={!!(touched.howDidDamageOccur && errors.howDidDamageOccur)}
                    />
                    {touched.howDidDamageOccur && errors.howDidDamageOccur ? (
                        <FormHelperText error={true}>
                            <>{errors.howDidDamageOccur}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            </StyledCardSection>
            <StepperControls onPrevious={handleGoBack} onNext={handleNext} onNextLabel={common.NEXT} />
        </StepWrapper>
    );
};

export default ClaimDetailsScreen;
